import {
  Checkbox,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
  useTheme,
} from '@mui/material';

import { Activity_Types_Enum_Enum } from '@graphql/__generated__/graphql';
import { FilterSelectIcon } from '@modules/_shared/components/feed-v2/components/ActivityFilters/FilterSelectIcon';
import {
  checkboxStyles,
  getFilterSelectStyles,
  menuItemStyles,
} from '@modules/_shared/components/feed-v2/components/ActivityFilters/filterSelectStyles';
import { ACTIVITY_ICONS } from '@modules/_shared/components/feed-v2/constants/activityConfig';
import { useTranslateActivityType } from '@modules/_shared/components/feed-v2/utils/activityHelpers';
import { useLocale } from '@shared/hooks/locale';

const FILTERABLE_ACTIVITY_TYPES: Activity_Types_Enum_Enum[] = [
  Activity_Types_Enum_Enum.Note,
  Activity_Types_Enum_Enum.Task,
  Activity_Types_Enum_Enum.Call,
  Activity_Types_Enum_Enum.Visit,
  Activity_Types_Enum_Enum.Email,
  Activity_Types_Enum_Enum.Assignment,
  Activity_Types_Enum_Enum.Requalification,
  Activity_Types_Enum_Enum.AgentOptIn,
  Activity_Types_Enum_Enum.AgentQuizCompleted,
  Activity_Types_Enum_Enum.Change,
  Activity_Types_Enum_Enum.LeadStatusAgent,
  Activity_Types_Enum_Enum.ModuleGranted,
  Activity_Types_Enum_Enum.ModuleRevoked,
] as const;

type ActivityTypeFilterProps = {
  selectedTypes: Activity_Types_Enum_Enum[];
  onFilter: (selectedTypes: Activity_Types_Enum_Enum[]) => void;
  activityCounts: Record<Activity_Types_Enum_Enum, number>;
};

export const ActivityTypeFilter = ({
  selectedTypes,
  onFilter,
  activityCounts,
}: ActivityTypeFilterProps) => {
  const { t } = useLocale();
  const theme = useTheme();

  const translateActivityLabel = useTranslateActivityType();

  return (
    <FormControl
      size="small"
      sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 0 }}
    >
      <Select<Activity_Types_Enum_Enum[]>
        multiple
        displayEmpty
        value={selectedTypes}
        onChange={e => onFilter(e.target.value as Activity_Types_Enum_Enum[])}
        IconComponent={FilterSelectIcon}
        input={<OutlinedInput />}
        renderValue={selected => {
          if (selected.length === 0) {
            return t('All activity types');
          }

          const firstSelectedAct = translateActivityLabel(selected[0]);
          if (selected.length === 1) {
            return firstSelectedAct;
          }

          return `${firstSelectedAct} +${selected.length - 1}`;
        }}
        MenuProps={{
          slotProps: {
            paper: { sx: { mt: 0.5 } },
          },
        }}
        sx={getFilterSelectStyles(selectedTypes.length > 0)}
      >
        {FILTERABLE_ACTIVITY_TYPES.map(type => {
          const Icon = ACTIVITY_ICONS[type];
          const label = translateActivityLabel(type);

          return (
            <MenuItem key={type} value={type} sx={menuItemStyles}>
              <Checkbox
                checked={selectedTypes.includes(type)}
                size="small"
                sx={checkboxStyles}
              />
              <Icon
                size={14}
                style={{
                  marginRight: 8,
                  color: (theme.vars ?? theme).palette.grey[600],
                }}
              />
              <Typography variant="body2">
                {label} ({activityCounts[type] ?? 0})
              </Typography>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};
